import * as Api from '@ViewModels';
import { css, type StyleDeclarationValue } from 'aphrodite';
import { parse as queryStringToObject } from 'query-string';
import * as React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { DefaultSupportedIndustries } from '../../../../models';
import { MastheadPortalId, type ISystemAutomationTemplate } from '../../../../models/AdminModels';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { useLocalNotificationService } from '../../../../models/LocalNotifications';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { useEventLogging } from '../../../../models/Logging';
import {
	useArchiveIndustryAutomationTemplateMutation,
	useIndustryAutomationTemplateQuery,
	usePublishIndustryAutomationTemplateMutation,
	useSetIndustryAutomationTemplateIndustriesMutation,
} from '../../../../queries/Admin';
import {
	type AutomationCanvasEditorActionBarRenderProps,
	AutomationCanvasEditor,
} from '../../../../shared/components/automation/AutomationCanvasEditor';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { MultiContainerHeader } from '../../../../web/components/MultiContainerHeader';
import { NavigationBreadcrumbsBar } from '../../../../web/components/NavigationBreadcrumbsBar';
import { type ISelectOption } from '../../../../web/components/DeprecatedSelect';
import { Button } from '../../../../web/components-v2/Button';
import { AdminDrawerToolbar, SideDrawer } from '../../../../web/components-v2/Drawer/AppDrawer';
import { useAutomationFlowTemplate } from '../../../../web/containers/AutomationFlows/hooks/useAutomationFlowTemplate';
import { type INavigationItemProps } from '../../../../web/containers/MainContainer';
import {
	DefaultIndustryDropdownOptions,
	IndustriesDropdown,
	type TemplateIndustryOption,
} from '../../../components/IndustriesDropdown';
import { AdminDashboardPageLayout } from '../../AdminDashboardPageLayout';
import { styleSheet } from './styles';

interface IProps extends INavigationItemProps {
	className?: string;
	styles?: StyleDeclarationValue[];
}

function getSelectedIndustries(selectedIndustryOptions: ISelectOption<TemplateIndustryOption>[]): Api.Industry[] {
	if (selectedIndustryOptions.length === 0 || selectedIndustryOptions[0].dataContext === 'All') {
		return DefaultSupportedIndustries;
	}

	return selectedIndustryOptions.map(option => option.dataContext as Api.Industry);
}

function WarningIcon() {
	return (
		<svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
			<path
				d='M8 1L15 14H1L8 1Z'
				stroke='currentColor'
				strokeWidth='1.5'
				strokeLinecap='round'
				strokeLinejoin='round'
				fill='none'
			/>
			<path d='M8 6v3' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
			<circle cx='8' cy='11.5' r='0.75' fill='currentColor' />
		</svg>
	);
}

export const EditIndustryAutomationTemplateCanvas: React.FC<IProps> = props => {
	const { className, styles = [] } = props;
	const location = useLocation();
	const { id: templateId, accountId } = useParams<{ id: string; accountId?: string }>();
	const history = useHistory();
	const { logEvent, logApiError } = useEventLogging('EditIndustryAutomationTemplateCanvas');
	const errorMessages = useErrorMessages();
	const { postNotification } = useLocalNotificationService<ISystemAutomationTemplate>();

	const impersonationContext = React.useMemo<Api.IImpersonationContext | undefined>(() => {
		return accountId ? { account: { id: accountId } } : undefined;
	}, [accountId]);

	const industryAutomationTemplateQuery = useIndustryAutomationTemplateQuery({ id: templateId });
	const industryAutomationTemplate = industryAutomationTemplateQuery.data ?? null;
	const { template, isLoading, isError, error, refetch } = useAutomationFlowTemplate({
		id: templateId,
		enabled: Boolean(templateId),
		impersonationContext,
	});

	const [selectedIndustry, setSelectedIndustry] = React.useState<Api.Industry>(null);
	const [selectedIndustryOptions, setSelectedIndustryOptions] = React.useState<ISelectOption<TemplateIndustryOption>[]>(
		[DefaultIndustryDropdownOptions.find(option => option.dataContext === 'All')]
	);

	React.useEffect(() => {
		if (!industryAutomationTemplate) {
			return;
		}

		const queryParams = queryStringToObject(location?.search || '');
		const industries = new Set<Api.Industry>(
			Object.keys(industryAutomationTemplate.industries || {}) as Api.Industry[]
		);
		const nextSelectedOptions = DefaultIndustryDropdownOptions.filter(option =>
			industries.has(option.dataContext as Api.Industry)
		);

		setSelectedIndustry((queryParams?.industry as Api.Industry) || null);
		setSelectedIndustryOptions(
			nextSelectedOptions.length > 0
				? nextSelectedOptions
				: [DefaultIndustryDropdownOptions.find(option => option.dataContext === 'All')]
		);
	}, [industryAutomationTemplate, location?.search]);

	const closeCanvas = React.useCallback(() => {
		history.push('/automations');
	}, [history]);

	const publishMutation = usePublishIndustryAutomationTemplateMutation({
		onError: publishError => {
			errorMessages.pushApiError(publishError);
			logApiError('Publish-Error', publishError);
		},
		onSuccess: data => {
			postNotification({
				info: data,
				topic: Topics.EDIT_SYSTEM_AUTOMATION_TEMPLATE,
			});
			closeCanvas();
		},
	});

	const archiveMutation = useArchiveIndustryAutomationTemplateMutation({
		onError: archiveError => {
			errorMessages.pushApiError(archiveError);
			logApiError('Archive-Error', archiveError);
		},
		onSuccess: data => {
			postNotification({
				info: data,
				topic: Topics.EDIT_SYSTEM_AUTOMATION_TEMPLATE,
			});
			closeCanvas();
		},
	});

	const setIndustriesMutation = useSetIndustryAutomationTemplateIndustriesMutation({
		onError: setIndustriesError => {
			errorMessages.pushApiError(setIndustriesError);
			logApiError('SetIndustries-Error', setIndustriesError);
		},
		onSuccess: data => {
			postNotification({
				info: data,
				topic: Topics.EDIT_SYSTEM_AUTOMATION_TEMPLATE,
			});
		},
	});

	const selectedStatus = selectedIndustry
		? industryAutomationTemplate?.industries?.[selectedIndustry]
		: Api.TemplateStatus.Draft;
	const isReadOnly = selectedStatus === Api.TemplateStatus.Archived;
	const isMutating = publishMutation.isPending || archiveMutation.isPending || setIndustriesMutation.isPending;

	const onSelectedIndustryOptionsChanged = React.useCallback(
		(nextSelectedIndustryOptions: ISelectOption<TemplateIndustryOption>[]) => {
			if (!industryAutomationTemplate) {
				return;
			}

			const previousSelectedIndustryOptions = selectedIndustryOptions;
			const industries = getSelectedIndustries(nextSelectedIndustryOptions);
			const includesSelectedIndustry = selectedIndustry ? industries.includes(selectedIndustry) : true;

			setSelectedIndustryOptions(nextSelectedIndustryOptions);
			if (!includesSelectedIndustry) {
				setSelectedIndustry(null);
			}

			logEvent('SetIndustries', { industries, templateId: industryAutomationTemplate.template.id });
			setIndustriesMutation.mutate(
				{
					templateId: industryAutomationTemplate.template.id,
					industries,
					status: Api.TemplateStatus.Draft,
					automationTemplate: template,
				},
				{
					onError: () => {
						setSelectedIndustryOptions(previousSelectedIndustryOptions);
						if (!includesSelectedIndustry) {
							setSelectedIndustry(selectedIndustry);
						}
					},
				}
			);
		},
		[industryAutomationTemplate, logEvent, selectedIndustry, selectedIndustryOptions, setIndustriesMutation, template]
	);

	const onPublish = React.useCallback(() => {
		if (!industryAutomationTemplate || !template) {
			return;
		}

		if (selectedIndustry) {
			logEvent('Publish', {
				industry: selectedIndustry,
				templateId: industryAutomationTemplate.template.id,
			});
			publishMutation.mutate({
				templateId: industryAutomationTemplate.template.id,
				industry: selectedIndustry,
				automationTemplate: template,
			});
			return;
		}

		const industries = getSelectedIndustries(selectedIndustryOptions);
		logEvent('PublishForIndustries', {
			industries,
			templateId: industryAutomationTemplate.template.id,
		});
		setIndustriesMutation.mutate({
			templateId: industryAutomationTemplate.template.id,
			industries,
			status: Api.TemplateStatus.Published,
			automationTemplate: template,
		});
	}, [
		industryAutomationTemplate,
		logEvent,
		publishMutation,
		selectedIndustry,
		selectedIndustryOptions,
		setIndustriesMutation,
		template,
	]);

	const onArchive = React.useCallback(() => {
		if (!industryAutomationTemplate || !selectedIndustry || !template) {
			return;
		}

		logEvent('Archive', {
			industry: selectedIndustry,
			templateId: industryAutomationTemplate.template.id,
		});
		archiveMutation.mutate({
			templateId: industryAutomationTemplate.template.id,
			industry: selectedIndustry,
			automationTemplate: template,
		});
	}, [archiveMutation, industryAutomationTemplate, logEvent, selectedIndustry, template]);

	const renderActionBar = React.useCallback(
		(actionBarProps: AutomationCanvasEditorActionBarRenderProps) => {
			if (!industryAutomationTemplate) {
				return null;
			}

			const isPublishingSelectedScope = selectedIndustry ? publishMutation.isPending : setIndustriesMutation.isPending;

			return (
				<div className={css(styleSheet.actionBar)}>
					<div className={css(styleSheet.actionBarLeft)}>
						<IndustriesDropdown
							onSelectedIndustryOptionsChanged={onSelectedIndustryOptionsChanged}
							selectedIndustryOptions={selectedIndustryOptions}
						/>
					</div>
					<div className={css(styleSheet.actionBarRight)}>
						{actionBarProps.hasDraft && (
							<div className={css(styleSheet.unpublishedBadge)}>
								<WarningIcon />
								<span>UNPUBLISHED CHANGES</span>
							</div>
						)}
						{selectedStatus === Api.TemplateStatus.Archived ? (
							<div className={css(styleSheet.statusText)}>Archived industry templates are view only.</div>
						) : null}
						{selectedStatus === Api.TemplateStatus.Draft ? (
							<Button variant='primary' onClick={onPublish} disabled={isMutating} isLoading={isPublishingSelectedScope}>
								Publish
							</Button>
						) : null}
						{selectedStatus === Api.TemplateStatus.Published && actionBarProps.hasDraft ? (
							<Button variant='primary' onClick={onPublish} disabled={isMutating} isLoading={isPublishingSelectedScope}>
								Publish Changes
							</Button>
						) : null}
						{selectedStatus === Api.TemplateStatus.Published && selectedIndustry ? (
							<Button
								variant='secondary'
								onClick={onArchive}
								disabled={isMutating}
								isLoading={archiveMutation.isPending}
							>
								Archive
							</Button>
						) : null}
					</div>
				</div>
			);
		},
		[
			archiveMutation.isPending,
			industryAutomationTemplate,
			isMutating,
			onArchive,
			onPublish,
			onSelectedIndustryOptionsChanged,
			publishMutation.isPending,
			selectedIndustry,
			selectedIndustryOptions,
			selectedStatus,
			setIndustriesMutation.isPending,
		]
	);

	const refetchAll = React.useCallback(() => {
		void industryAutomationTemplateQuery.refetch();
		void refetch();
	}, [industryAutomationTemplateQuery, refetch]);

	const combinedError = React.useMemo(() => {
		if (error) {
			return error;
		}
		if (industryAutomationTemplateQuery.isError) {
			const queryError = industryAutomationTemplateQuery.error;
			if (queryError instanceof Error) {
				return queryError;
			}
			return { message: 'Failed to load automation template.' };
		}
		return null;
	}, [error, industryAutomationTemplateQuery.error, industryAutomationTemplateQuery.isError]);

	return (
		<div
			className={`${css(styleSheet.container, ...(styles || []))} edit-industry-automation-template-canvas ${className || ''}`}
		>
			<AutomationCanvasEditor
				templateId={templateId || ''}
				template={template}
				isLoading={isLoading || industryAutomationTemplateQuery.isLoading}
				isError={isError || industryAutomationTemplateQuery.isError}
				error={combinedError}
				refetch={refetchAll}
				impersonationContext={impersonationContext}
				onPublishSuccess={closeCanvas}
				onDeleteSuccess={closeCanvas}
				contentStyles={[styleSheet.contentExtras]}
				isReadOnly={isReadOnly}
				showTestingContacts={false}
				renderActionBar={renderActionBar}
				renderLoading={message => (
					<AdminDashboardPageLayout sidebarChildren={null}>
						<div className={css(styleSheet.contentLoading)}>
							<div className={css(styleSheet.loadingContainer)} role='status' aria-live='polite'>
								<LoadingSpinner type='large' />
								<p className={css(styleSheet.loadingText)}>{message}</p>
							</div>
						</div>
					</AdminDashboardPageLayout>
				)}
				renderError={(message, onRetry) => (
					<AdminDashboardPageLayout sidebarChildren={null}>
						<div className={css(styleSheet.contentLoading)}>
							<div className={css(styleSheet.errorContainer)} role='alert' aria-live='assertive'>
								<h2 className={css(styleSheet.errorTitle)}>Failed to load automation</h2>
								<p className={css(styleSheet.errorMessage)}>{message}</p>
								<Button variant='primary' onClick={onRetry} aria-label='Retry loading automation'>
									Retry
								</Button>
							</div>
						</div>
					</AdminDashboardPageLayout>
				)}
			>
				{({ contentArea, drawerPanels, drawerToolbarButtons }) => (
					<AdminDashboardPageLayout
						sidebarChildren={
							<SideDrawer>
								{drawerPanels}
								<AdminDrawerToolbar>{drawerToolbarButtons}</AdminDrawerToolbar>
							</SideDrawer>
						}
					>
						<MultiContainerHeader
							appBarHeader={
								<NavigationBreadcrumbsBar
									currentLocationName={
										industryAutomationTemplate?.template?.name || template?.name || 'Edit Automation'
									}
								/>
							}
							fullscreenHeader={industryAutomationTemplate?.template?.name || template?.name || 'Edit Automation'}
							appBarPortalDestinationId={MastheadPortalId}
						/>
						{contentArea}
					</AdminDashboardPageLayout>
				)}
			</AutomationCanvasEditor>
		</div>
	);
};
